import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  getPatientDocuments,
  uploadPatientDocument,
  deletePatientDocument,
  downloadPatientDocument,
  fileToBase64,
} from "../../../../services/patientService";
import {
  CloudUploadIcon,
  DeleteIcon,
  ViewIcon,
  FileCheckIcon,
} from "../../../../Icons";
import CollapsibleSection, {
  useCollapseOpenForCount,
} from "../CollapsibleSection";
import useConfirm from "../../../../hooks/useConfirm";
import { displayDateUS } from "../../../../utils/dateUtils";
import { PATIENT_SECTION_TONE, PATIENT_THEME } from "../../patientTheme";
import {
  PATIENT_DOC_UPLOAD_ACCEPT,
  PATIENT_DOC_UPLOAD_TYPES_LABEL,
  validatePatientUploadFileClient,
} from "../../patientUploadTypes";

const AUTH_DOCS_CATEGORY = "Authorization";

const AUTH_DOCS_TONE = PATIENT_SECTION_TONE;

const fieldClass = `w-full h-9 px-2.5 text-sm border border-slate-200 rounded-md bg-white outline-none transition-shadow ${PATIENT_THEME.fieldFocus}`;
const labelClass = "block text-[11px] font-semibold text-slate-600 mb-1";
const outlineActionBtn =
  "inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-medium rounded-md border bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const outlineAmberBtn = `${outlineActionBtn} ${PATIENT_THEME.button.outline}`;
const outlineNeutralBtn = `${outlineActionBtn} text-slate-700 border-slate-300 hover:bg-slate-50`;

/**
 * Authorization Documents — claim-required uploads on Auth & Schedule.
 * Reuses patient documents API with category=Authorization.
 */
const PatientAuthorizationDocumentsSection = ({
  patientId,
  chartId,
  canEdit,
  onRefresh,
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const { confirm, confirmDialog } = useConfirm();

  const loadDocs = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await getPatientDocuments(patientId, {
        category: AUTH_DOCS_CATEGORY,
        ...(chartId ? { chartId } : {}),
      });
      setDocuments(res.result?.documents || res.documents || []);
    } catch (err) {
      toast.error(err.message || "Failed to load authorization documents");
    } finally {
      setLoading(false);
    }
  }, [patientId, chartId]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const [sectionOpen, onSectionOpenChange] = useCollapseOpenForCount(
    documents.length,
    `${patientId}:${chartId || ""}`,
  );
  const resetForm = () => {
    setDocumentName("");
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) {
      setFile(null);
      return;
    }
    const check = validatePatientUploadFileClient(picked);
    if (!check.ok) {
      toast.error(check.message);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setFile(picked);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    const check = validatePatientUploadFileClient(file);
    if (!check.ok) {
      toast.error(check.message);
      return;
    }
    setUploading(true);
    try {
      const fileBase64 = await fileToBase64(file);
      await uploadPatientDocument(patientId, {
        documentName: documentName.trim() || null,
        documentCategory: AUTH_DOCS_CATEGORY,
        documentType: AUTH_DOCS_CATEGORY,
        documentDate: null,
        fileBase64,
        fileName: file.name,
        ...(chartId ? { chartId: Number(chartId) } : {}),
      });
      toast.success("Authorization document uploaded");
      resetForm();
      await loadDocs();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openFile = async (docId) => {
    try {
      const res = await downloadPatientDocument(patientId, docId, "view");
      const url = res.result?.url || res.url;
      if (url) window.open(url, "_blank");
    } catch (err) {
      toast.error(err.message || "Unable to open file");
    }
  };

  const handleDelete = async (doc) => {
    const ok = await confirm({
      title: "Delete authorization document?",
      message: `"${doc.documentName || doc.originalName || "This document"}" will be permanently removed. This cannot be undone.`,
    });
    if (!ok) return;
    try {
      await deletePatientDocument(patientId, doc.id);
      toast.success("Document deleted");
      await loadDocs();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <CollapsibleSection
      title="Authorization Documents"
      subtitle="(Required to submit with Claim)"
      icon={<FileCheckIcon size={14} />}
      tone={AUTH_DOCS_TONE}
      open={sectionOpen}
      onOpenChange={onSectionOpenChange}
      badge={
        <span className="inline-flex items-center justify-center h-5 px-1.5 rounded text-[10px] font-semibold bg-slate-600 text-white tabular-nums">
          {documents.length} file{documents.length === 1 ? "" : "s"}
        </span>
      }
    >
      {canEdit && (
        <div className="px-3 py-3 border-b border-slate-100 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2.5 items-end">
            <div className="lg:col-span-5">
              <label className={labelClass}>
                File <span className="text-red-500">*</span>
              </label>
              <input
                ref={fileRef}
                type="file"
                accept={PATIENT_DOC_UPLOAD_ACCEPT}
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className={`${outlineNeutralBtn} shrink-0`}
                >
                  Select file
                </button>
                <span className="text-xs text-slate-500 truncate min-w-0">
                  {file ? file.name : "No file selected"}
                </span>
              </div>
              <p className="mt-1 text-[10px] text-slate-500">
                {PATIENT_DOC_UPLOAD_TYPES_LABEL} (incl. iPhone photos) · max 20
                MB
              </p>
            </div>
            <div className="lg:col-span-5">
              <label className={labelClass}>File Name</label>
              <input
                type="text"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="Optional label"
                className={fieldClass}
                maxLength={255}
              />
            </div>
            <div className="lg:col-span-2 flex sm:justify-end">
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading}
                className={`${outlineAmberBtn} w-full lg:w-auto`}
              >
                {uploading ? (
                  <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CloudUploadIcon size={14} />
                )}
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-y-auto max-h-72">
        {loading ? (
          <p className="px-3 py-4 text-center text-sm text-slate-400 animate-pulse">
            Loading documents...
          </p>
        ) : documents.length === 0 ? (
          <p className="px-3 py-4 text-center text-sm text-slate-500">
            No authorization documents yet
          </p>
        ) : (
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[30%]" />
              <col className="w-[22%]" />
              <col className="w-[14%]" />
              <col className="w-[20%]" />
              <col className="w-[14%]" />
            </colgroup>
            <thead
              className={`sticky top-0 z-10 ${PATIENT_THEME.tableHead.row}`}
            >
              <tr
                className={`border-b text-left text-[11px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
              >
                <th className="px-3 py-2 font-semibold">File Name</th>
                <th className="px-3 py-2 font-semibold hidden sm:table-cell">
                  Upload By
                </th>
                <th className="px-3 py-2 font-semibold hidden md:table-cell">
                  Role
                </th>
                <th className="px-3 py-2 font-semibold hidden sm:table-cell">
                  Upload At
                </th>
                <th className="px-3 py-2 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr
                  key={doc.id}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70"
                >
                  <td className="px-3 py-2">
                    <p className="font-medium text-slate-900 truncate">
                      {doc.documentName || doc.originalName || "—"}
                    </p>
                  </td>
                  <td className="px-3 py-2 text-slate-600 truncate hidden sm:table-cell">
                    {doc.uploadedByName || "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-600 truncate hidden md:table-cell">
                    {doc.uploadedByRole || "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap hidden sm:table-cell tabular-nums">
                    {displayDateUS(doc.uploadedAt) || "—"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      className="p-1 text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 rounded inline-flex"
                      onClick={() => openFile(doc.id)}
                      title="Open"
                    >
                      <ViewIcon size={14} />
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        className="p-1 text-red-600 hover:bg-red-50 rounded inline-flex"
                        onClick={() => handleDelete(doc)}
                        title="Delete"
                      >
                        <DeleteIcon size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {confirmDialog}
    </CollapsibleSection>
  );
};

export default PatientAuthorizationDocumentsSection;
